import { useState } from 'react';
import { Loader2, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { usePermission } from '@/hooks/usePermission';
import { PERMISSIONS } from '@features/shared/constants/permissions';
import type { SalesOrderDto } from '../types/salesOrder.types';
import { SalesOrderStatus } from '../types/salesOrder.types';
import { salesOrderService } from '../services/salesOrderService';
import { useSalesOrderStore } from '../store/salesOrderStore';

// 86d3gdj5q — ONE-TIME, DISPOSABLE corrective tool. Re-rounds each line's VAT-inclusive unit to
// the centavo and pushes the corrected ex-VAT rate to the already-posted NetSuite SO so it ties
// to the customer PO, then syncs the DOP snapshot. Per-SO, admin-run.
//
// To RETIRE once the backlog is corrected: flip ENABLED to false, or delete this file + its single
// usage in SalesOrderReadOnlySummary. NOT a permanent feature.
const ENABLED = true;

function peso(n: number | null | undefined): string {
  return n == null
    ? '—'
    : `₱${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function SalesOrderCorrectPriceButton({ draft }: { draft: SalesOrderDto }) {
  // Gated by the submit permission (the disposable tool reuses it — no dedicated perm).
  const canCorrect = usePermission(PERMISSIONS.SALES_ORDERS_SUBMIT_SALES_ORDER);
  const refreshActiveDraft = useSalesOrderStore((s) => s.refreshActiveDraft);
  const [running, setRunning] = useState(false);

  // Show only for a posted (Submitted) SO. The BE re-checks the LIVE NetSuite status (only
  // Pending Approval is correctable) and returns a clear message for anything approved/beyond.
  if (!ENABLED || !canCorrect) return null;
  if (draft.status !== SalesOrderStatus.Submitted || !draft.netSuiteTranId) return null;

  const onClick = async () => {
    setRunning(true);
    const toastId = toast.loading(`Correcting ${draft.netSuiteTranId} — updating NetSuite…`, {
      description: 'Re-rounding each line and verifying against the PO. This can take a minute.',
    });
    try {
      const r = await salesOrderService.correctPrice(draft.id);
      const so = r.netSuiteTranId ?? 'Sales order';
      if (!r.success) {
        toast.error('Price correction did not complete', {
          id: toastId,
          description: r.message || 'See the order in NetSuite.',
        });
      } else if (r.skippedNetSuiteLines > 0) {
        // DOP lines corrected, but the SO has lines added directly in NetSuite that we don't touch.
        toast.warning(`${so} — DOP lines corrected, ${r.skippedNetSuiteLines} external line(s) skipped`, {
          id: toastId,
          description: r.message,
        });
        void refreshActiveDraft(draft.id);
      } else if (r.corrected) {
        const changed = r.lines.filter((l) => l.changed).length;
        toast.success(`${so} corrected — now matches the PO`, {
          id: toastId,
          description: `${changed} line(s) re-rounded · total ${peso(r.oldTotal)} → ${peso(r.newTotal)}.`,
        });
        // Refresh so any posting/status metadata on the panel reflects the latest read.
        void refreshActiveDraft(draft.id);
      } else {
        toast.info('Nothing to correct', {
          id: toastId,
          description: r.message || 'DOP lines already match the PO to the centavo.',
        });
      }
    } catch (err) {
      toast.error('Price correction failed', {
        id: toastId,
        description: err instanceof Error ? err.message : 'Unexpected error.',
      });
    } finally {
      setRunning(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={onClick}
      disabled={running}
      className="gap-1.5"
      title="Re-round this SO's line prices to the centavo and push the fix to NetSuite"
    >
      {running ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Wand2 className="h-3.5 w-3.5" />
      )}
      {running ? 'Correcting…' : 'Correct Price'}
    </Button>
  );
}
